import { Button, Modal } from "react-bootstrap";
import { FaDownload, FaExternalLinkAlt } from "react-icons/fa";

export type PreviewableDocument = {
  url: string;
  name: string;
  type: "image" | "pdf" | "other";
};

type DocumentPreviewModalProps = {
  document: PreviewableDocument | null;
  fallbackTitle?: string;
  onHide: () => void;
  t?: (key: string) => string;
};

const label = (
  t: DocumentPreviewModalProps["t"],
  key: string,
  fallback: string,
) => {
  const translated = t?.(key);
  return translated && translated !== key ? translated : fallback;
};

export default function DocumentPreviewModal({
  document,
  fallbackTitle = "Document",
  onHide,
  t,
}: DocumentPreviewModalProps) {
  const title = document?.name || fallbackTitle;

  return (
    <Modal show={!!document} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "#f8fafc" }}>
        {document?.type === "image" && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <img
              src={document.url}
              alt={document.name}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
                background: "#fff",
              }}
            />
          </div>
        )}

        {document?.type === "pdf" && (
          <iframe
            title={document.name}
            src={document.url}
            style={{
              width: "100%",
              height: "75vh",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              background: "#fff",
            }}
          />
        )}

        {document?.type === "other" && (
          <div className="text-center py-5">
            <p className="text-muted mb-3">
              {label(t, "preview_not_available", "Apercu non disponible")}
            </p>
            <Button as="a" href={document.url} target="_blank" rel="noreferrer">
              <FaExternalLinkAlt className="me-2" />
              {label(t, "open_in_new_tab", "Ouvrir")}
            </Button>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {document && (
          <>
            <Button
              as="a"
              variant="outline-secondary"
              href={document.url}
              target="_blank"
              rel="noreferrer"
            >
              <FaExternalLinkAlt className="me-2" />
              {label(t, "open_in_new_tab", "Ouvrir dans un nouvel onglet")}
            </Button>
            <Button
              as="a"
              variant="warning"
              href={document.url}
              download={document.name}
            >
              <FaDownload className="me-2" />
              {label(t, "download", "Telecharger")}
            </Button>
          </>
        )}
        <Button variant="secondary" onClick={onHide}>
          {label(t, "close", "Fermer")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
